import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, BarChart3, Brain, Crown, LogOut, Search, Shield, UserCog } from "lucide-react";

import MainAdmin from "../admin/MainAdmin";
import MasterIntelligenceDashboard from "./MasterIntelligenceDashboard";
import MasterOperationalDashboard from "./MasterOperationalDashboard";

const API = process.env.REACT_APP_API_URL || "";

function UserAccessManager({ setActiveComponent, onLoginSuccess }) {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const persistSession = (data) => {
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("user_id", data.user_id);
    localStorage.setItem("username", data.username);
    localStorage.setItem("email", data.email);
    localStorage.setItem("phone_number", data.phone_number);
    localStorage.setItem("role", data.role);
    localStorage.setItem("providerId", data.provider_id);
    localStorage.setItem("addresses", JSON.stringify(data.addresses || []));
    localStorage.setItem("impersonated_by_user_id", data.impersonated_by_user_id);
  };

  const authConfig = () => {
    const token = localStorage.getItem("token");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/api/master/users`, {
        ...authConfig(),
        params: { q: query, limit: 80 },
      });
      setUsers(data.users || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "No se pudieron cargar usuarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterAccount = async (userId) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/master/impersonate`, { user_id: userId }, authConfig());
      persistSession(data);
      onLoginSuccess?.(data.user_id, data.username, data.email, data.phone_number, data.role, data.addresses, data.provider_id);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "No se pudo entrar a esta cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setActiveComponent("MasterHome")}
            className="h-10 w-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">User Access</h1>
            <p className="text-sm text-white/70">Master-only account controls</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900">Entrar a una cuenta</h2>
          <p className="text-sm text-slate-600 mt-2">
            Esta accion emite una sesion del usuario seleccionado y queda auditada como impersonacion master.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") loadUsers();
                }}
                placeholder="Buscar por nombre, correo o telefono"
                className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={loadUsers}
              disabled={loading}
              className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              Buscar
            </button>
          </div>

          {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="mt-5 overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">Rol</th>
                  <th className="px-3 py-2 text-left">Correo</th>
                  <th className="px-3 py-2 text-left">Verificado</th>
                  <th className="px-3 py-2 text-right">Accion</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-semibold text-slate-900">{user.username}</td>
                    <td className="px-3 py-2 text-slate-600">{user.role}</td>
                    <td className="px-3 py-2 text-slate-600">{user.email}</td>
                    <td className="px-3 py-2 text-slate-600">{user.email_verified ? "Si" : "No"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => enterAccount(user.id)}
                        disabled={loading}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        Entrar
                      </button>
                    </td>
                  </tr>
                ))}
                {!users.length && !loading && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-slate-500">No hay usuarios.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MasterHome({ username, onLogout, setActiveComponent }) {
  const actions = [
    {
      id: "OperationalDashboard",
      label: "Master Operational Dashboard",
      description: "Daily command center: lifecycle, COD exposure, drivers, merchants, districts, and event truth",
      icon: BarChart3,
      onClick: () => setActiveComponent("OperationalDashboard"),
    },
    {
      id: "ExecutiveIntelligence",
      label: "Executive Intelligence",
      description: "Company-level metrics, AI insights, and strategic decisions",
      icon: Brain,
      onClick: () => setActiveComponent("ExecutiveIntelligence"),
    },
    {
      id: "AdminOperations",
      label: "Admin Operations",
      description: "Control tower, accounting, routes, drivers, packages, and integrations",
      icon: Shield,
      onClick: () => setActiveComponent("AdminOperations"),
    },
    {
      id: "UserAccess",
      label: "User Access",
      description: "Master-only access and role management workspace",
      icon: UserCog,
      onClick: () => setActiveComponent("UserAccess"),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-brand-gold/20 flex items-center justify-center">
              <Crown className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold">FAST MAX Master</h1>
              <p className="text-sm text-white/70">{username || "Master"} - Global access</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="h-10 px-4 rounded-lg bg-white/10 hover:bg-white/20 flex items-center gap-2 text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" />
            Salir
          </button>
        </div>
      </div>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:border-brand-primary hover:shadow-md text-left transition"
              >
                <div className="h-11 w-11 rounded-lg bg-brand-primary/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-brand-primary" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">{action.label}</h2>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{action.description}</p>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default function MainMaster(props) {
  const [activeComponent, setActiveComponent] = useState("OperationalDashboard");

  if (activeComponent === "AdminOperations") {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-slate-950 text-white px-4 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setActiveComponent("MasterHome")}
            className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 flex items-center gap-2 text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Master Home
          </button>
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Crown className="w-4 h-4 text-brand-gold" />
            Admin Operations
          </div>
        </div>
        <MainAdmin {...props} role="master" />
      </div>
    );
  }

  if (activeComponent === "ExecutiveIntelligence") {
    return (
      <MasterIntelligenceDashboard
        username={props.username}
        setActiveComponent={setActiveComponent}
      />
    );
  }

  if (activeComponent === "OperationalDashboard") {
    return (
      <MasterOperationalDashboard
        username={props.username}
        onLogout={props.onLogout}
        setActiveComponent={setActiveComponent}
      />
    );
  }

  if (activeComponent === "UserAccess") {
    return <UserAccessManager setActiveComponent={setActiveComponent} onLoginSuccess={props.onLoginSuccess} />;
  }

  return (
    <MasterHome
      username={props.username}
      onLogout={props.onLogout}
      setActiveComponent={setActiveComponent}
    />
  );
}
